import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { ArrowLeft, Copy, Share2, Gift, DollarSign, Users, CheckCircle, Clock, UserPlus, Sparkles, ExternalLink, Info, Building2 } from 'lucide-react';
import { toast } from 'sonner';

import { formatDateMonthDay } from '../../utils/timeFormat';
const TalentReferrals = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [copying, setCopying] = useState(false);
  const [manualCopyText, setManualCopyText] = useState('');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await api.get('/referrals/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Failed to load referral dashboard:', error);
      toast.error('Failed to load referral data');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text) => {
    if (!text) { toast.error('Nothing to copy'); return; }
    setCopying(true);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard!');
      } else {
        // Fallback for non-secure contexts / older browsers
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        if (ok) {
          toast.success('Copied to clipboard!');
        } else {
          throw new Error('execCommand failed');
        }
      }
    } catch {
      // Last-resort fallback — show a manual copy modal
      setManualCopyText(text);
    } finally {
      setTimeout(() => setCopying(false), 1000);
    }
  };

  const shareReferral = async () => {
    if (navigator.share && dashboard?.referral_link) {
      try {
        await navigator.share({
          title: 'Join iEvntür',
          text: `Join iEvntür using my referral link and we both earn rewards!`,
          url: dashboard.referral_link
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          copyToClipboard(dashboard.referral_link);
        }
      }
    } else {
      copyToClipboard(dashboard?.referral_link || '');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'signed_up': { label: 'Signed Up', color: 'bg-blue-100 text-blue-700', icon: UserPlus },
      'profile_completed': { label: 'Profile Complete', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
      'first_gig_completed': { label: 'First Gig Done', color: 'bg-amber-100 text-amber-700', icon: Sparkles },
      'payout_triggered': { label: 'Paid!', color: 'bg-green-100 text-green-700', icon: DollarSign }
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-100 text-gray-700', icon: Clock };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getProgressSteps = (referral) => {
    const steps = [
      { key: 'signed_up', label: 'Signed Up', done: !!referral.signed_up_at },
      { key: 'profile_completed', label: 'Profile Complete', done: !!referral.profile_completed_at },
      { key: 'first_gig_completed', label: 'First Gig', done: !!referral.first_gig_completed_at },
      { key: 'payout_triggered', label: 'Payout', done: !!referral.payout_triggered_at }
    ];
    return steps;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="talent-referrals-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button variant="ghost" onClick={() => navigate('/talent/home')} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-cyan-500 to-pink-500 rounded-xl p-6 md:p-8 text-white mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Gift className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">Refer & Earn (Up to $100)</h1>
              <p className="text-white/80">Earn $25 per talent · $100 per brand</p>
            </div>
          </div>

          {/* Reward Breakdown */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
              <Users className="w-5 h-5 mx-auto mb-1 text-white/80" />
              <p className="text-xl font-bold">$25</p>
              <p className="text-xs text-white/70">Per talent referred</p>
              <p className="text-[10px] text-white/50 mt-0.5">Paid within 30 days after they complete their first shift.</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3 text-center">
              <Building2 className="w-5 h-5 mx-auto mb-1 text-white/80" />
              <p className="text-xl font-bold">$100</p>
              <p className="text-xs text-white/70">Per brand/business referred</p>
              <p className="text-[10px] text-white/50 mt-0.5">Paid within 30 days after their first completed booking.</p>
            </div>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-4">
            <div className="text-sm text-white/80 mb-2">Your Referral Code</div>
            <div className="flex items-center gap-2">
              <code className="text-2xl font-mono font-bold tracking-widest" data-testid="referral-code">
                {dashboard?.referral_code || '---'}
              </code>
              <Button
                variant="ghost"
                size="sm"
                className="text-white hover:bg-white/20"
                onClick={() => copyToClipboard(dashboard?.referral_code || '')}
                data-testid="copy-code-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 bg-white/10 rounded-lg p-2">
              <Input
                value={dashboard?.referral_link || ''}
                readOnly
                className="bg-transparent border-0 text-white placeholder:text-white/50 text-sm"
                data-testid="referral-link-input"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                className="bg-white text-cyan-600 hover:bg-white/90"
                onClick={() => copyToClipboard(dashboard?.referral_link || '')}
                disabled={copying}
                data-testid="copy-link-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
              <Button
                variant="secondary"
                className="bg-white/20 text-white hover:bg-white/30"
                onClick={shareReferral}
                data-testid="share-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Earned</p>
                  <p className="text-3xl font-bold text-green-600" data-testid="total-earned">
                    ${dashboard?.stats?.total_earned?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending Rewards</p>
                  <p className="text-3xl font-bold text-amber-600" data-testid="pending-earnings">
                    ${dashboard?.stats?.pending_earnings?.toFixed(2) || '0.00'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Referrals</p>
                  <p className="text-3xl font-bold text-cyan-600" data-testid="total-referrals">
                    {dashboard?.stats?.total_referrals || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
                  <Users className="w-6 h-6 text-cyan-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <Card className="bg-white mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="w-5 h-5 text-cyan-500" />
              How It Works
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="talent" className="w-full">
              <TabsList className="grid grid-cols-2 w-full mb-4">
                <TabsTrigger value="talent">Refer Talent ($25)</TabsTrigger>
                <TabsTrigger value="business">Refer a Brand ($100)</TabsTrigger>
              </TabsList>
              <TabsContent value="talent">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center mb-3 text-cyan-600 font-bold">1</div>
                    <h4 className="font-medium mb-1">Share Your Link</h4>
                    <p className="text-sm text-muted-foreground">Send to friends who want to work events</p>
                  </div>
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center mb-3 text-cyan-600 font-bold">2</div>
                    <h4 className="font-medium mb-1">They Sign Up</h4>
                    <p className="text-sm text-muted-foreground">Your friend creates their talent account</p>
                  </div>
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center mb-3 text-cyan-600 font-bold">3</div>
                    <h4 className="font-medium mb-1">First Shift Done</h4>
                    <p className="text-sm text-muted-foreground">They complete their first successful shift</p>
                  </div>
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-3 text-green-600 font-bold">$</div>
                    <h4 className="font-medium mb-1">You Earn $25</h4>
                    <p className="text-sm text-muted-foreground">Paid within 30 days after they complete their first shift.</p>
                  </div>
                </div>
              </TabsContent>
              <TabsContent value="business">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center mb-3 text-pink-600 font-bold">1</div>
                    <h4 className="font-medium mb-1">Share Your Link</h4>
                    <p className="text-sm text-muted-foreground">Send to brands or businesses that need event staff</p>
                  </div>
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center mb-3 text-pink-600 font-bold">2</div>
                    <h4 className="font-medium mb-1">They Sign Up</h4>
                    <p className="text-sm text-muted-foreground">The business creates a client account</p>
                  </div>
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="w-10 h-10 bg-pink-100 rounded-full flex items-center justify-center mb-3 text-pink-600 font-bold">3</div>
                    <h4 className="font-medium mb-1">First Booking Done</h4>
                    <p className="text-sm text-muted-foreground">They complete their first event booking</p>
                  </div>
                  <div className="flex flex-col items-center text-center p-4">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-3 text-green-600 font-bold">$</div>
                    <h4 className="font-medium mb-1">You Earn $100</h4>
                    <p className="text-sm text-muted-foreground">4x more for bringing in new business!</p>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Referrals List */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle>Your Referrals</CardTitle>
            <CardDescription>Track the progress of people you've referred</CardDescription>
          </CardHeader>
          <CardContent>
            {!dashboard?.referrals || dashboard.referrals.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-muted-foreground mb-2">No referrals yet</p>
                <p className="text-sm text-muted-foreground">Share your link to start earning!</p>
              </div>
            ) : (
              <div className="space-y-4" data-testid="referrals-list">
                {dashboard.referrals.map((referral) => (
                  <div 
                    key={referral.id} 
                    className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
                    data-testid="referral-item"
                  >
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                          <UserPlus className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium">{referral.referred_name || 'New User'}</p>
                          <p className="text-sm text-muted-foreground">
                            {referral.referred_type === 'talent' ? 'Talent' : 'Client'} • 
                            Joined {referral.created_at ? formatDateMonthDay(referral.created_at) : 'Recently'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-green-600">${referral.bonus_amount?.toFixed(2)}</p>
                          <p className="text-xs text-muted-foreground">Reward</p>
                        </div>
                        {getStatusBadge(referral.status)}
                      </div>
                    </div>

                    {/* Progress Tracker */}
                    <div className="mt-4 pt-4 border-t">
                      <div className="flex items-center justify-between">
                        {getProgressSteps(referral).map((step, idx) => (
                          <React.Fragment key={step.key}>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className={`flex flex-col items-center ${step.done ? 'text-green-600' : 'text-gray-400'}`}>
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step.done ? 'bg-green-100' : 'bg-gray-100'}`}>
                                      {step.done ? <CheckCircle className="w-4 h-4" /> : <span className="text-xs">{idx + 1}</span>}
                                    </div>
                                    <span className="text-xs mt-1 hidden sm:block">{step.label}</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{step.label}: {step.done ? 'Complete' : 'Pending'}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            {idx < 3 && (
                              <div className={`flex-1 h-0.5 mx-2 ${step.done && getProgressSteps(referral)[idx + 1]?.done ? 'bg-green-300' : 'bg-gray-200'}`} />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Manual Copy Modal — fallback when clipboard API is blocked */}
      <Dialog open={!!manualCopyText} onOpenChange={(o) => !o && setManualCopyText('')}>
        <DialogContent data-testid="manual-copy-modal">
          <DialogHeader>
            <DialogTitle>Copy your referral link</DialogTitle>
            <DialogDescription>
              Your browser blocked automatic copying. Select the text below and copy it manually (Ctrl/Cmd + C).
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={manualCopyText}
            readOnly
            className="w-full h-24 p-2 border rounded font-mono text-sm"
            onFocus={(e) => e.target.select()}
            data-testid="manual-copy-textarea"
          />
          <DialogFooter>
            <Button onClick={() => setManualCopyText('')} data-testid="manual-copy-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TalentReferrals;
